import {Component, DestroyRef, NgZone, OnDestroy, ViewEncapsulation} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ApiService} from '../../../../shared/services/api.service';
import {EnrollService} from '../../../../shared/services/enroll.service';
import {Subscription, TimeoutError} from 'rxjs';
import {DevicesService, LangService, LoaderService, NavigateService} from '@satoris/ngx-satoris';
import {NfcService} from '../../../../shared/services/nfc.service';
import {ScanService} from '../../../../shared/services/scan.service';
import {SecureStorageService} from 'src/app/shared/services/secure-storage.service';
import {environment} from '../../../../../environments/environment';
import {InitializeService} from 'src/app/shared/services/initialize.service';
import {parseError} from 'src/app/shared/utils/string';
import {PrivacyScreen} from '@capacitor-community/privacy-screen';

/* activation-step-ask-pin assumes the same number when it decides whether to show the counter at all */
const CARD_PIN_TRIES_FULL = 3;

@Component({
  selector: 'app-activation-step-nfc-scan-id-card',
  templateUrl: './activation-step-nfc-scan-id-card.component.html',
  styleUrls: ['./activation-step-nfc-scan-id-card.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class ActivationStepNfcScanIdCardComponent implements OnDestroy {

  subject: string[] = [];
  progress = 0;
  speed = 1;
  certsWarn = false;

  private sub: Subscription;
  private errSub: Subscription;
  /* iOS reports a lost card twice, as the session event and as the command's rejection; a second dialog closes the first, whose empty result reads as a refusal to retry */
  private failureHandled = false;

  constructor(public nav: NavigateService,
    private destroyRef: DestroyRef,
    public nfc: NfcService,
    public api: ApiService,
    public enroll: EnrollService,
    private zone: NgZone,
    private lang: LangService,
    private loader: LoaderService,
    private secure: SecureStorageService,
    private scan: ScanService,
    private device: DevicesService,
    public initialize: InitializeService) {
    if(this.device.isDevices('cordova') && environment.production) PrivacyScreen.disable();
    this.nfc.claimIdCard = undefined;
    if(this.api.userInfo.usesLoginOtp || !this.api.userInfo.nationalNumber) {
      this.start(this.api.userRole.isAdmin);
    } else {
      this.loader.loading(true, {type: 'info', message: 'nfc.demo', isWaiting: true, waitTimer: 5000}).then(() => {
        this.enroll.navigate('next');
      });
    }

    if(this.api.userInfo.nationalNumber && this.api.userRole.isCustomer) {
      this.api.setPrincipalMode = true;
    }
  }

  ngOnDestroy() {
    this.sub?.unsubscribe();
    this.errSub?.unsubscribe();
    this.initialize.isScanning = false;
    if(this.device.isDevices('cordova')) {
      this.nfc.stop();
    }
    if(this.device.isDevices('cordova') && environment.production) PrivacyScreen.enable();
    this.api.setPrincipalMode = false;
  }

  start(admin?: boolean) {
    this.failureHandled = false;
    try {
      this.nfc.init();
      this.nfc.scan().then(() => {
        /* scan() resolves on a native callback, so this can land after ngOnDestroy, on a service that outlives the component */
        this.sub?.unsubscribe();
        this.sub = this.nfc.cardFound$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
          this.scanCard(admin);
        });
        this.errSub?.unsubscribe();
        this.errSub = this.nfc.nfcError$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.onNfcError(admin));
      });
    } catch (err) {
      this.loader.loading(true, {
        type: 'error',
        message: this.lang.transform('nfc.read.nfc_notfound')
      }).then(() => this.exitFlow());
    }
  }

  private get exitTarget() {
    return this.api.setCardRenewalMode || this.api.setPrincipalMode ? 'user' : this.api.userRole.isCustomer ? 'activation' : 'manage-user';
  }

  private exitFlow() {
    this.nav.to(this.exitTarget);
  }

  private backToCan() {
    if(!this.enroll.backTo(this.enroll.step.ASK_CAN)) this.enroll.navigate('previous');
  }

  onNfcError(admin?: boolean, messageKey = 'error.nfc') {
    /* a session error while a command is in flight is that command's failure, and the lost-card path rescans in place instead of leaving the flow */
    if(messageKey === 'error.nfc' && this.initialize.isScanning) return this.onCardLost();
    if(this.failureHandled) return;
    this.failureHandled = true;
    return this.zone.run(() => {
      this.progress = 0;
      this.initialize.isScanning = false;
      this.nfc.stop();
      return this.loader.loading(true, {type: 'error', message: this.lang.transform(messageKey), btnLabel: this.lang.transform('btn.retry')}).then((retry: boolean) => {
        if(retry) {
          this.start(admin);
        } else {
          this.exitFlow();
        }
      });
    });
  }

  onReadFailed(admin?: boolean) {
    if(this.failureHandled) return;
    this.failureHandled = true;
    return this.zone.run(() => {
      this.progress = 0;
      this.initialize.isScanning = false;
      this.nfc.stop();
      return this.loader.loading(true, {
        type: 'error',
        message: this.lang.transform('nfc.error.readFailed'),
        btnLabel: this.lang.transform('btn.retry'),
        custom: {closeBtnLabel: this.lang.transform('cancel')},
        additionalButtons: [{label: this.lang.transform('nfc.read.changeCan'), color: 'secondary', border: true, action: () => 'changeCan', closeOnAction: true}]
      }).then((result: any) => {
        if(result === true) {
          this.start(admin);
        } else if(result === 'changeCan') {
          this.backToCan();
        } else {
          this.exitFlow();
        }
      });
    });
  }

  /* a card that slipped mid-signature spends no try, so rescan in place the way a failed ICAO read does, instead of sending the citizen back through the face check */
  private onCardLost() {
    if(this.failureHandled) return;
    this.failureHandled = true;
    this.zone.run(() => {
      this.progress = 0;
      this.sub?.unsubscribe();
      this.nfc.stop();
      this.loader.loading(true, {type: 'error', message: this.lang.transform('nfc.read.nfcLost')}).then(() => {
        this.initialize.isScanning = false;
        if(this.api.userRole.isAdmin) {
          this.start(true);
        } else {
          this.start();
        }
      });
    });
  }

  scanCard(_admin?: boolean) {
    this.failureHandled = false;
    this.sub.unsubscribe();
    this.zone.run(() => {
      this.progress = 50;
    });
    this.initialize.isScanning = true;
    this.nfc.getCertificate(true, true, this.enroll.form.controls[this.enroll.fc.CAN].value).then(() => {
      this.nfc.readIcao(false, this.enroll.form.controls[this.enroll.fc.CAN].value).then(() => this.zone.run(() => this.onIcaoRead(_admin))).catch(() => this.onCardLost());
    }).catch((err) => {
      if(err === 'wrongCard') {
        this.onNfcError(_admin, 'nfc.error.wrongCard');
      } else {
        this.onReadFailed(_admin);
      }
    });
  }

  onIcaoRead(_admin?: boolean) {
    this.progress = 80;
    this.checkIcao();
    if(!_admin) {
      // customer with PIN
      const typedPin = this.enroll.form.controls[this.enroll.fc.PIN]?.value;
      const pin = typedPin || this.enroll.serverPin;
      if(pin && this.enroll.form.controls[this.enroll.fc.CAN]?.value) {
        /* an issued PIN cannot be corrected on the spot, so every try it would eat leads to a blocked card rather than to an enrolment */
        if(!typedPin && this.nfc.icao?.pin !== CARD_PIN_TRIES_FULL) {
          return this.onCompleteError('resetProcess', this.withTriesLeft('nfc.read.serverPinRisk'));
        }
        this.submit(pin);
      } else { // customer without PIN, facecheck on next
        /* the face check exists only to fetch the issued PIN, and it mails one out on the way */
        if(this.nfc.icao?.pin !== CARD_PIN_TRIES_FULL && !this.enroll.hasStep(this.enroll.step.ASK_PIN)) {
          return this.onCompleteError('resetProcess', this.withTriesLeft('nfc.read.serverPinRisk'));
        }
        this.progress = 100;
        this.speed = 0.1;
        this.enroll.navigate('next');
      }
    // admin with fingerprint
    } else if(this.enroll.form?.controls[this.enroll.fc.FINGERPRINT]?.value.image && this.nfc.icao.personalDetails?.personalNumber){
      this.api.testFinger(+this.scan.userScanData.data.sig, this.enroll.form.controls[this.enroll.fc.FINGERPRINT].value.image, this.nfc.icao.personalDetails?.personalNumber, +this.enroll.form.controls[this.enroll.fc.FINGERPRINT].value.finger).then(() => {
        this.api.activateSc(+this.scan.userScanData.data.sig, this.enroll.form.controls[this.enroll.fc.DEVICE_XY].value.x, this.enroll.form.controls[this.enroll.fc.DEVICE_XY].value.y, this.enroll.form.controls[this.enroll.fc.DEVICE_NAME].value || 'myDevice', this.nfc.icao.sodRaw,
          this.nfc.icao.mrz.raw, this.nfc.icao.face.raw, this.nfc.icao.personalDetails.raw, this.nfc.icao.documentDetails.raw, this.enroll.form.controls[this.enroll.fc.SIGNATURE].value, this.nfc.icao.personalDetails.personalNumber).then(() => {
          this.progress = 100;
          this.speed = 0.1;
        }).catch(err => {
          const error = parseError(err);
          console.error(error);
          this.onCompleteError('adminActivate', this.nniAlreadyUsed(error.baseError) || error.tradError);
        });
      }).catch(err => {
        console.error(err);
        const error = parseError(err);
        const errorMessage = error.tradError?.includes('client.extended.noFingerAuth') ? 'client.extended.noFingerAuth' : error.tradError;
        this.onCompleteError('adminFingerError', errorMessage);
      });
    } else { // admin without fingerprint
      this.api.activateSc(+this.scan.userScanData.data.sig, this.enroll.form.controls[this.enroll.fc.DEVICE_XY].value.x, this.enroll.form.controls[this.enroll.fc.DEVICE_XY].value.y, this.enroll.form.controls[this.enroll.fc.DEVICE_NAME].value || 'myDevice', this.nfc.icao.sodRaw,
        this.nfc.icao.mrz.raw, this.nfc.icao.face.raw, this.nfc.icao.personalDetails.raw, this.nfc.icao.documentDetails.raw, this.enroll.form.controls[this.enroll.fc.SIGNATURE].value, this.nfc.icao.personalDetails.personalNumber).then(() => {
        this.progress = 100;
        this.speed = 0.1;
      }).catch(err => {
        const error = parseError(err);
        this.onCompleteError('adminActivate', this.nniAlreadyUsed(error.baseError) || error.tradError);
      });
    }
  }

  /* the card's own count, and the only place the citizen ever learns it outside the ASK_PIN screen, which no production config has */
  private withTriesLeft(key: string) {
    const tries = this.nfc.icao?.pin;
    return tries === undefined ? this.lang.transform(key) : this.lang.transform(key) + ' ' + this.lang.transform('nfc.read.self.scan.pinRemain') + ': ' + tries;
  }

  /* the server appends the owner's masked address after the key, so neither parseError's tradError nor a bare transform resolves it */
  private nniAlreadyUsed(baseError: string) {
    const [key, ...masked] = (baseError || '').split(' ');
    return key === 'client.extended.nniAlreadyUsed' ? (this.lang.transform(key + '.DYN.server') + ' ' + masked.join(' ')).trim() : undefined;
  }

  submit(pin: string) {
    const pem = this.nfc.pkiCert.toString('base64');
    this.api.rearmSc(this.api.userInfo.accountName, pem, [pem]).then(params => {
      this.sign(pin, params.sigStatusChallenge, params.salt);
    }).catch((err) => {
      const error = parseError(err);
      if(err instanceof TimeoutError) {
        return this.onCompleteError('serverError');
      }
      this.onCompleteError('serverError', error.tradError);
    });
  }

  sign(pin: string, sigStatusChallenge: string, salt: string) {
    this.nfc.sign(true, pin, sigStatusChallenge, this.enroll.form.controls[this.enroll.fc.CAN].value).then(pkcs12 => this.secure.retrieveSecretKeyAndCerts().then(([xyCert, certChain]) => {
      if((this.device.isDevices('cordova-android') && (!xyCert || !certChain)) || (this.device.isDevices('cordova-ios') && !xyCert)) this.certsWarn = true;
      return this.api.confirmSc(this.api.userInfo.id,
        salt,
        pkcs12,
        this.secure.fullJwk.x,
        this.secure.fullJwk.y,
        this.enroll.form.controls[this.enroll.fc.DEVICE_NAME].value || 'myDevice',
        this.nfc.icao.sodRaw,
        this.nfc.icao.mrz.raw,
        this.nfc.icao.face.raw,
        this.nfc.icao.personalDetails.raw,
        this.nfc.icao.documentDetails.raw,
        this.enroll.form.controls[this.enroll.fc.SIGNATURE].value,
        undefined,
        xyCert,
        certChain);
    }).then(() => {
      this.zone.run(()=>{
        if(this.certsWarn){
          this.loader.loading(true, {type: 'warn', message: 'warning.no_attestation'}).then(() => {
            this.progress = 100;
            this.speed = 0.1;
          });
        } else {
          this.progress = 100;
          this.speed = 0.1;
        }
      });
    }).catch((err: any) => {
      const error = parseError(err);
      if(err instanceof TimeoutError) {
        return this.onCompleteError('serverError');
      }
      if(error.baseError === 'client.extended.customerExists') {
        return this.onCompleteError('resetProcess', error.tradError);
      }
      // No face detected on document chip photo - retrying won't help
      if(error.baseError === 'client.extended.noFaceOnDocument') {
        return this.onCompleteError('resetProcess', error.tradError);
      }
      const nniTaken = this.nniAlreadyUsed(error.baseError);
      if(nniTaken) {
        return this.onCompleteError('resetProcess', nniTaken);
      }
      /* the card signed before this call was made, so nothing it answers is evidence about the PIN */
      this.onCompleteError('serverError', error.tradError);
    })).catch((err: any) => {
      /* only a refusal carries the card's count, so its absence is what tells a lost card apart from a wrong PIN */
      const cardRefusedPin = typeof err?.pinTries === 'number';
      if(this.nfc.icao?.pin === 0) {
        return this.onCompleteError('noMorePin');
      }
      if(!cardRefusedPin) {
        return this.onCardLost();
      }
      /* asking the server again returns the very same PIN, so a retry would spend another of the card's tries for nothing */
      if(!this.enroll.form.controls[this.enroll.fc.PIN]?.value) {
        return this.onCompleteError('resetProcess', this.withTriesLeft('nfc.read.serverPinMismatch'));
      }
      this.onCompleteError('pin', this.withTriesLeft('nfc.read.wrongPin'));
    });
  }

  onComplete() {
    this.enroll.navigate('next');
  }

  onCompleteError(error?: 'pin' | 'noMorePin' | 'adminActivate' | 'resetProcess' | 'certError' | 'serverError' | 'adminFingerError', serverError?: string) {
    this.nfc.stop();
    this.zone.run(() => {
      this.initialize.isScanning = false;
      this.progress = 0;
      let msgError: string;
      let redirect: string;
      switch(error) {
        case 'pin':
          this.enroll.form.controls[this.enroll.fc.PIN].setValue(undefined);
          msgError = 'nfc.read.wrongPin';
          redirect = 'nfc-read-pin';
          break;
        case 'noMorePin':
          msgError = 'nfc.read.noMorePinTries';
          redirect = this.exitTarget;
          break;
        case 'adminActivate':
          msgError = 'nfc.read.adminActivate';
          redirect = 'manage-user';
          break;
        case 'resetProcess':
          redirect = this.exitTarget;
          break;
        case 'certError':
          msgError = 'nfc.error.empty_certificate';
          redirect = 'nfc-read-can';
          break;
        case 'serverError':
          msgError = 'call.timeout';
          redirect = 'nfc-read-can';
          break;
        case 'adminFingerError':
          msgError = 'call.timeout';
          redirect = 'previous-one';
          break;
        default :
          this.enroll.form.controls[this.enroll.fc.CAN].setValue(undefined);
          msgError = 'nfc.read.self.canError';
          redirect = 'nfc-read-can';
      }
      this.loader.loading(true, {
        type: 'error',
        message: serverError || this.lang.transform(msgError)
      }).then(()=>{
        if(redirect === 'nfc-read-pin' || redirect === 'previous-one') {
          this.enroll.navigate('previous');
        } else if(redirect === 'nfc-read-can') {
          this.backToCan();
        } else {
          this.nav.to(redirect);
        }
      });
    });
  }

  checkIcao() {
    if(!this.nfc.claimIdCard) {
      this.nfc.pkiCert?.subject.typesAndValues.forEach((item: any) => {
        this.subject.push(item.value.valueBlock.value);
      });
      this.nfc.claimIdCard = undefined;
      this.nfc.claimIdCard = {
        date_of_birth: this.nfc.icao.mrz.dateOfBirth,
        expiry_date: this.nfc.icao.mrz.dateOfExpiry,
        first_name: [this.subject[4]],
        middle_name: [],
        last_name: [this.subject[5]],
        gender: this.nfc.icao.mrz.gender,
        image_url: this.nfc.icao.face.data,
        mrz: {line1: '', line2: '', line3: ''},
        nationality: this.nfc.icao.mrz.nationality,
        place_of_birth: this.nfc.icao.personalDetails.birthPlace,
        can: +this.enroll.form.controls[this.enroll.fc.CAN].value,
        signature: this.nfc.signature,
        height: 0,
        profession: this.nfc.icao.personalDetails.profession,
        doc_nr: this.nfc.icao.mrz.documentNumber,
        personal_address: this.nfc.icao.personalDetails.personalAddress,
        telephone_number: '+' + this.nfc.icao.personalDetails.telephoneNumber.substring(0, 3) + ' ' + this.nfc.icao.personalDetails.telephoneNumber.substring(4, this.nfc.icao.personalDetails.telephoneNumber.length).replace(/(.{3})/g, '$1-'),
        personal_number: Number(this.nfc.icao.personalDetails.personalNumber)
      };
    }
  }
}
